import type { IAudienceHeartbeatAggregate } from '@lucimark/shared/models/playback-heartbeat'
import { AUDIENCE_HEARTBEAT_AGGREGATE_VERSION } from '@lucimark/shared/models/playback-heartbeat'
import { PLAYBACK_HEARTBEAT_INTERVAL_MS } from '@/constants'
import {
  audienceCvPersonConfig,
  shouldSkipAudienceCvByMotion
} from '@/helpers/audienceCvPersonConfig'
import {
  armPlaybackHeartbeatCadence,
  type PlaybackHeartbeatCadenceHandle
} from '@/helpers/playbackHeartbeatJitter'
import {
  detectPersonBoxesFromVideo,
  isAudienceCvPersonModelUnavailable,
  resetAudienceCvPersonDetectCache
} from '@/helpers/audienceCvPersonDetect'
import { createAudienceCvPersonTracker } from '@/helpers/audienceCvPersonTracker'
import { queueAudienceHeartbeatAggregate } from '@/helpers/audienceHeartbeatAttachment'
import { meanAbsLumaDiff } from '@/helpers/audienceMotionLumaDiff'
import { isAudienceSecondaryCameraAcquireSuppressed } from '@/helpers/enumerateAudienceVideoInputDevices'

/** Downscaled luma frame side (small = cheap; enough for coarse motion). */
const CAPTURE_W = 80
const CAPTURE_H = 60

/** How often we diff frames while the pipeline is running. */
const SAMPLE_INTERVAL_MS = 2_500

/** Normalized mean abs luma diff above this counts as “active” for `presenceScore`. */
const MOTION_ACTIVE_THRESHOLD = 0.028

/** Consecutive below-threshold diffs before a motion “session” ends (reduces flicker double-counts). */
const MOTION_SESSION_END_BELOW_STREAK = 2

/** Maps typical kiosk motion into ~0–1; `min(1, meanDiff * this)`. */
const MOTION_SCORE_SCALE = 1 / 0.11

const LEN = CAPTURE_W * CAPTURE_H

/** Heartbeat aggregate motion/presence from one window’s accumulators (`motionCount` ≥ 1). */
function motionPresenceScoresFromWindow(
  motionSum: number,
  motionCount: number,
  activeSamples: number
): { motionScore: number, presenceScore: number } {
  const meanMotion = motionSum / motionCount
  const motionScore = Math.min(1, Math.max(0, meanMotion * MOTION_SCORE_SCALE))
  const presenceScore = Math.min(1, Math.max(0, activeSamples / motionCount))
  return { motionScore, presenceScore }
}

function fillLumaFromImageData(src: Uint8ClampedArray, out: Uint8Array): void {
  for (let i = 0, j = 0; j < out.length; i += 4, j++) {
    out[j] = Math.round(0.299 * src[i] + 0.587 * src[i + 1] + 0.114 * src[i + 2])
  }
}

export interface IAudienceAnalyticsMotionPipeline {
  /** Start timers when `shouldRun` becomes true; stop and release camera when false. */
  sync: () => void
  dispose: () => void
}

/** Running-window preview of the same motion/presence scores queued on heartbeat flush (partial window until flush). */
export interface IAudienceAnalyticsLiveStats {
  /** True when a MediaStream is active and wired to the capture video element. */
  capturing: boolean
  /** Running mean-based motion score for the current heartbeat window; null until at least one diff sample. */
  motionScore: number | null
  /** Fraction of diff samples above the active threshold; null until at least one diff sample. */
  presenceScore: number | null
  /** Number of frame-to-frame diffs accumulated in the current window. */
  diffSamples: number
  /**
   * When person-CV is enabled: true if TF.js / COCO-SSD failed to initialize (no qualified-track metrics until reset).
   */
  personCvModelUnavailable?: boolean
}

/**
 * Web or Android WebView: samples USB/builtin camera at low rate, estimates motion / activity, and queues
 * `IAudienceHeartbeatAggregate` once per playback-heartbeat window for upload on the next heartbeat.
 *
 * Pauses while the document is hidden, hardware tier is `low`, operator WebRTC preview holds the camera, or a short
 * cooldown after preview ends (shared with heartbeat enumeration — avoids immediately re-opening the camera).
 */
export function createAudienceAnalyticsMotionPipeline(deps: {
  shouldRun: () => boolean
  /** Tenant + tier gate for throttled COCO-SSD person detection on the capture video. */
  personCvEnabled: () => boolean
  isWebRtcPreviewActive: () => boolean
  getVideoInputDeviceId: () => string | null
  getCapacityTier: () => 'low' | 'mid' | 'high' | null
  /**
   * Optional: invoked when running-window stats change and with `null` when the pipeline stops.
   * No extra timers — emissions follow the sample interval, window flush, camera release, and start/stop only.
   */
  onLiveStats?: (stats: IAudienceAnalyticsLiveStats | null) => void
}): IAudienceAnalyticsMotionPipeline {
  const cvTracker = createAudienceCvPersonTracker({
    cvPersonModelId: audienceCvPersonConfig.modelId,
    ...audienceCvPersonConfig.tracker
  })
  let running = false
  /** Bumped on stop, start, and window flush so in-flight `sampleFrame` async work cannot mutate state or emit after a new epoch. */
  let pipelineEpoch = 0
  /** Jittered send cadence (#1257); aggregation uses actual elapsed since {@link windowStartedAtMs}. */
  let windowCadence: PlaybackHeartbeatCadenceHandle | null = null
  let windowStartedAtMs = 0
  let sampleTimer: ReturnType<typeof setInterval> | null = null
  let stream: MediaStream | null = null
  let video: HTMLVideoElement | null = null
  let ctx: CanvasRenderingContext2D | null = null
  const prevLuma = new Uint8Array(LEN)
  const nextLuma = new Uint8Array(LEN)
  let havePrev = false

  let motionSum = 0
  let motionCount = 0
  let activeSamples = 0
  /** Debounced motion-burst tracking for `motionBasedSessionCount` (not people). */
  let belowThresholdStreak = 0
  let inMotionBurst = false
  let motionSessionCount = 0
  let visibilityHandler: (() => void) | null = null
  let lastCvSampleMs = 0

  function emitLiveStats() {
    if (deps.onLiveStats == null) {
      return
    }
    if (!running) {
      deps.onLiveStats(null)
      return
    }
    const capturing = stream != null && stream.active && video != null
    let motionScore: number | null = null
    let presenceScore: number | null = null
    if (motionCount > 0) {
      const s = motionPresenceScoresFromWindow(motionSum, motionCount, activeSamples)
      motionScore = s.motionScore
      presenceScore = s.presenceScore
    }
    deps.onLiveStats({
      capturing,
      motionScore,
      presenceScore,
      diffSamples: motionCount,
      personCvModelUnavailable: deps.personCvEnabled() && isAudienceCvPersonModelUnavailable()
    })
  }

  function releaseCamera() {
    havePrev = false
    if (stream != null) {
      for (const t of stream.getTracks()) {
        try {
          t.stop()
        }
        catch {
          // ignore
        }
      }
      stream = null
    }
    if (video != null) {
      video.srcObject = null
      video = null
    }
    ctx = null
    if (running && deps.onLiveStats != null) {
      emitLiveStats()
    }
  }

  function resetWindowStats() {
    motionSum = 0
    motionCount = 0
    activeSamples = 0
    havePrev = false
    belowThresholdStreak = 0
    inMotionBurst = false
    motionSessionCount = 0
  }

  function flushWindowToHeartbeatQueue() {
    const windowEndMs = Date.now()
    const elapsedMs = windowStartedAtMs > 0
      ? Math.max(1, windowEndMs - windowStartedAtMs)
      : PLAYBACK_HEARTBEAT_INTERVAL_MS
    const intervalSeconds = Math.max(1, Math.round(elapsedMs / 1000))
    const windowStartMs = windowEndMs - elapsedMs
    windowStartedAtMs = windowEndMs
    pipelineEpoch += 1
    const snapSum = motionSum
    const snapCount = motionCount
    const snapActive = activeSamples
    const snapSessions = motionSessionCount
    resetWindowStats()

    let cvExtras: Pick<IAudienceHeartbeatAggregate, 'cvPersonModelId' | 'personQualifiedTrackCount' | 'personDwellSeconds'> = {}
    if (deps.personCvEnabled()) {
      const m = cvTracker.endWindow(windowStartMs, windowEndMs, intervalSeconds)
      if (m.personQualifiedTrackCount > 0 || m.personDwellSeconds > 0) {
        cvExtras = {
          cvPersonModelId: m.cvPersonModelId,
          personQualifiedTrackCount: m.personQualifiedTrackCount,
          personDwellSeconds: m.personDwellSeconds
        }
      }
    }
    else {
      cvTracker.reset()
    }

    if (snapCount < 1 && Object.keys(cvExtras).length === 0) {
      emitLiveStats()
      return
    }

    const aggregate: IAudienceHeartbeatAggregate = {
      version: AUDIENCE_HEARTBEAT_AGGREGATE_VERSION,
      intervalSeconds,
      ...cvExtras
    }
    if (snapCount >= 1) {
      const { motionScore, presenceScore } = motionPresenceScoresFromWindow(snapSum, snapCount, snapActive)
      const dwellRawSec = snapActive * (SAMPLE_INTERVAL_MS / 1000)
      const estimatedActiveDwellSeconds = Math.min(intervalSeconds, Math.round(dwellRawSec * 1000) / 1000)
      aggregate.motionScore = motionScore
      aggregate.presenceScore = presenceScore
      aggregate.estimatedActiveDwellSeconds = estimatedActiveDwellSeconds
      aggregate.motionBasedSessionCount = snapSessions
    }
    queueAudienceHeartbeatAggregate(aggregate)
    emitLiveStats()
  }

  async function ensureCamera(): Promise<boolean> {
    if (typeof navigator === 'undefined' || navigator.mediaDevices?.getUserMedia == null) {
      return false
    }
    if (isAudienceSecondaryCameraAcquireSuppressed()) {
      releaseCamera()
      return false
    }
    if (stream != null && stream.active && video != null) {
      return true
    }
    releaseCamera()
    const trimmed = deps.getVideoInputDeviceId()?.trim() ?? ''
    const videoConstraints: MediaTrackConstraints | boolean = trimmed !== ''
      ? { deviceId: { exact: trimmed } }
      : true
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints, audio: false })
    }
    catch {
      if (trimmed === '') {
        return false
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false })
      }
      catch {
        return false
      }
    }
    if (deps.isWebRtcPreviewActive() || isAudienceSecondaryCameraAcquireSuppressed()) {
      releaseCamera()
      return false
    }
    const v = document.createElement('video')
    v.muted = true
    v.playsInline = true
    v.setAttribute('playsinline', '')
    v.srcObject = stream
    try {
      await v.play()
    }
    catch {
      releaseCamera()
      return false
    }
    video = v
    const c = document.createElement('canvas')
    c.width = CAPTURE_W
    c.height = CAPTURE_H
    const cctx = c.getContext('2d', { willReadFrequently: true })
    if (cctx == null) {
      releaseCamera()
      return false
    }
    ctx = cctx
    return true
  }

  function sampleFrame() {
    if (!running) {
      return
    }
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      releaseCamera()
      return
    }
    if (deps.isWebRtcPreviewActive()) {
      releaseCamera()
      return
    }
    if (isAudienceSecondaryCameraAcquireSuppressed()) {
      releaseCamera()
      return
    }
    if (deps.getCapacityTier() === 'low') {
      releaseCamera()
      return
    }
    const epochAtSchedule = pipelineEpoch
    void (async () => {
      try {
        const ok = await ensureCamera()
        if (!ok || epochAtSchedule !== pipelineEpoch || !running || ctx == null || video == null) {
          return
        }
        if (!deps.personCvEnabled()) {
          cvTracker.reset()
        }
        if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
          return
        }
        try {
          ctx.drawImage(video, 0, 0, CAPTURE_W, CAPTURE_H)
          const snap = ctx.getImageData(0, 0, CAPTURE_W, CAPTURE_H)
          fillLumaFromImageData(snap.data, nextLuma)
        }
        catch {
          return
        }
        if (epochAtSchedule !== pipelineEpoch || !running) {
          return
        }
        if (havePrev) {
          const d = meanAbsLumaDiff(prevLuma, nextLuma)
          motionSum += d
          motionCount += 1
          const active = d >= MOTION_ACTIVE_THRESHOLD
          if (active) {
            belowThresholdStreak = 0
            if (!inMotionBurst) {
              motionSessionCount += 1
              inMotionBurst = true
            }
            activeSamples += 1
          }
          else {
            belowThresholdStreak += 1
            if (belowThresholdStreak >= MOTION_SESSION_END_BELOW_STREAK) {
              inMotionBurst = false
              belowThresholdStreak = 0
            }
          }
        }
        prevLuma.set(nextLuma)
        havePrev = true
        if (
          deps.personCvEnabled()
          && epochAtSchedule === pipelineEpoch
          && running
          && video != null
          && (typeof document === 'undefined' || document.visibilityState === 'visible')
        ) {
          const nowCv = Date.now()
          const cvCadence = audienceCvPersonConfig.detect.sampleIntervalMs
          if (nowCv - lastCvSampleMs >= cvCadence) {
            lastCvSampleMs = nowCv
            const skipByMotion = shouldSkipAudienceCvByMotion(motionCount, motionSum, activeSamples)
            if (!skipByMotion) {
              const ep = epochAtSchedule
              const minScore = audienceCvPersonConfig.detect.minScore
              void detectPersonBoxesFromVideo(video, minScore).then((boxes) => {
                if (ep !== pipelineEpoch || !running) {
                  return
                }
                cvTracker.processFrame(boxes, Date.now())
                if (running && deps.onLiveStats != null) {
                  emitLiveStats()
                }
              })
            }
          }
        }
      }
      finally {
        if (running && epochAtSchedule === pipelineEpoch) {
          emitLiveStats()
        }
      }
    })()
  }

  function stopInternal() {
    pipelineEpoch += 1
    running = false
    windowCadence?.clear()
    windowCadence = null
    windowStartedAtMs = 0
    if (sampleTimer != null) {
      clearInterval(sampleTimer)
      sampleTimer = null
    }
    if (visibilityHandler != null && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }
    releaseCamera()
    resetWindowStats()
    lastCvSampleMs = 0
    cvTracker.reset()
    resetAudienceCvPersonDetectCache()
    deps.onLiveStats?.(null)
  }

  function startInternal() {
    if (typeof window === 'undefined') {
      return
    }
    running = true
    resetWindowStats()
    pipelineEpoch += 1
    windowStartedAtMs = Date.now()
    // Jitter only the send timer; flush aggregates over actual elapsed (#1257).
    windowCadence = armPlaybackHeartbeatCadence({
      baseIntervalMs: PLAYBACK_HEARTBEAT_INTERVAL_MS,
      onTick: () => {
        flushWindowToHeartbeatQueue()
      }
    })
    sampleTimer = setInterval(() => {
      sampleFrame()
    }, SAMPLE_INTERVAL_MS)
    visibilityHandler = () => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        releaseCamera()
      }
    }
    document.addEventListener('visibilitychange', visibilityHandler)
    sampleFrame()
    emitLiveStats()
  }

  return {
    sync: () => {
      const want
        = deps.shouldRun()
          && typeof navigator !== 'undefined'
          && typeof document !== 'undefined'
      if (want && !running) {
        startInternal()
      }
      else if (!want && running) {
        stopInternal()
      }
    },
    dispose: () => {
      stopInternal()
    }
  }
}
